<template>
  <v-autocomplete
    :outlined="outlined"
    v-model="selected"
    :items="siteList"
    :menu-props="{ maxHeight: '400', zIndex: zindex }"
    :label="label"
    :multiple="multiple"
    small-chips
    deletable-chips
    :item-text="itemText"
    :item-value="itemValue"
    :dense="dense"
  ></v-autocomplete>
</template>

<script>
import isArray from "lodash/isArray";
import isEqual from "lodash/isEqual";

import {
  computed,
  onMounted,
  reactive,
  set,
  watch,
} from "@vue/composition-api";

import useProxy from "@/hooks/useProxy";
import useQueryString from "@/hooks/useQueryString";

import { URL_PARAM_NAMES } from "@/utils/constants";

const COMPONENT_NAME = "SiteFilter";

/**
 * Either call this component giving an `initialValue` (int[])
 *  and listening on `valueChanged` event,
 * Or call it specifying a `store`
 *  (and eventually `storeGetter`/`storeUpdater`)
 */
export default {
  name: COMPONENT_NAME,
  props: {
    initialValue: {
      type: [Number, Array],
      validator(values) {
        if (isArray(values)) {
          return (
            values.length === 0 ||
            values.every((value) => typeof value === "number")
          );
        }

        return !isNaN(values);
      },
    },
    zindex: {
      type: Number,
      default: 13,
    },
    store: {
      type: String,
      validator(value) {
        /* List of stores having :
        - getters["<store>/<storeGetter>"]
        - dispatch("<store>/<storeUpdater>", payload)
        */
        return [
          "audience",
          "aiFactory",
          "c2c",
          "dataAvailability",
          "finance",
          "gd",
          "general",
          "linkMessApps",
          "newsRadar",
          "premium",
          "regie",
          "webPerf",
          "acquisition",
        ].includes(value);
      },
    },
    siteListGetter: {
      type: String,
      default: "common/getSiteList",
    },
    storeGetter: {
      type: String,
      default: "getSites",
    },
    storeUpdater: {
      type: String,
      default: "updateSites",
    },
    multiple: {
      type: Boolean,
      default: true,
    },
    dependsOnSiteGroups: {
      type: Boolean,
      default: true,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    outlined: {
      type: Boolean,
      default: true,
    },
    noUrl: {
      type: Boolean,
      default: false,
    },
    itemValue: {
      type: String,
      default: "id",
    },
    itemText: {
      type: String,
      default: "name",
    },
  },
  setup(props, { emit }) {
    const { store } = useProxy();

    const { addQueryStringParam, synchronizeFilterWithQueryString } =
      useQueryString();

    const page = reactive({
      isInitialized: false,
      initialUrlValue: props.multiple ? [] : "", // in case we need to load from url params
    });

    // =>  METHODS

    // =>  COMPUTED
    const siteList = computed(() => store.getters[props.siteListGetter]);
    const viaStore = computed(() => props.store !== undefined);
    const initialValue = computed(() =>
      props.noUrl
        ? props.initialValue
        : page.initialUrlValue ?? props.initialValue
    );

    const selected = computed({
      get() {
        if (viaStore.value) {
          return store.getters[`${props.store}/${props.storeGetter}`];
        }

        return initialValue.value;
      },
      set(e) {
        if (viaStore.value === false) {
          if (!isEqual(e, initialValue.value)) {
            emit("valueChanged", e);
          }

          return;
        }

        store.dispatch(`${props.store}/${props.storeUpdater}`, e);
      },
    });

    const label = computed(() =>
      props.multiple ? "Sélectionner les sites" : "Sélectionner un site"
    );

    // =>  WATCH
    watch(selected, (newValue) => {
      if (page.isInitialized && !props.noUrl) {
        // Only update user changes, not initialization
        addQueryStringParam({
          param: URL_PARAM_NAMES[COMPONENT_NAME],
          value: newValue,
          is_multiple: props.multiple,
          is_integer: true,
          // is_boolean: false,
        });
      }
    });

    // =>  HOOKS
    onMounted(async () => {
      await store.dispatch(props.siteListGetter);

      if (!props.noUrl) {
        set(
          page,
          "initialUrlValue",
          await synchronizeFilterWithQueryString({
            dispatcher: viaStore.value
              ? `${props.store}/${props.storeUpdater}`
              : "",
            param: URL_PARAM_NAMES[COMPONENT_NAME],
            value: selected.value,
            is_multiple: props.multiple,
            is_integer: true,
            // is_boolean: false,
            dependsOn: props.dependsOnSiteGroups
              ? URL_PARAM_NAMES["SiteGroupFilter"]
              : undefined,
          })
        );
      }

      if (
        viaStore.value &&
        props.multiple &&
        store.getters["common/getIsSiteRestricted"] &&
        (!selected.value || selected.value.length === 0)
      ) {
        store.dispatch(
          `${props.store}/${props.storeUpdater}`,
          store.getters["common/getAccessibleSiteIds"]
        );
      }

      page.isInitialized = true;
    });

    // =>  DATA
    return {
      label,
      siteList,
      selected,
    };
  },
};
</script>

<style scoped>
[role="menu"] {
  top: 233px !important;
  right: 0 !important;
}
</style>
